export const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? 'http://127.0.0.1:5001/get-bank-sheets-staging/us-central1' : '')

// External API functions (for API key users)
export interface Transaction {
  id: string
  accountId: string
  amount: number
  date: string
  name: string
  category?: string[]
  pending: boolean
  transactionId: string
  merchantName?: string
  paymentChannel?: string
  primaryCategory?: string
  detailedCategory?: string
}

export interface TransactionsResponse {
  transactions: Transaction[]
  total: number
  hasMore: boolean
  nextCursor?: string
}

export interface Account {
  id: string
  name: string
  type: string
  subtype: string
  mask: string
  institutionName: string
  balance?: {
    available: number
    current: number
    limit?: number
  }
  isActive: boolean
  lastSync?: string
}

export interface AccountsResponse {
  accounts: Account[]
  total: number
}

export interface Spreadsheet {
  id: string
  name: string
  url: string
  lastSync?: string
  isActive: boolean
  syncFrequency?: string
  createdAt: string
}

export interface SheetsResponse {
  spreadsheets: Spreadsheet[]
  total: number
}

// External API functions (for API key users)
export const getTransactions = async (
  apiKey: string,
  params?: {
    limit?: number
    cursor?: string
    startDate?: string
    endDate?: string
    accountId?: string
  }
): Promise<TransactionsResponse> => {
  const searchParams = new URLSearchParams()
  if (params?.limit) searchParams.append('limit', params.limit.toString())
  if (params?.cursor) searchParams.append('cursor', params.cursor)
  if (params?.startDate) searchParams.append('startDate', params.startDate)
  if (params?.endDate) searchParams.append('endDate', params.endDate)
  if (params?.accountId) searchParams.append('accountId', params.accountId)

  const url = `${API_BASE}/publicApiEndpoints/getTransactions${searchParams.toString() ? `?${searchParams.toString()}` : ''}`

  return fetch(url, {
    method: 'GET',
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`API request failed: ${res.status} ${res.statusText}`)
    }
    return res.json()
  })
}

// MCP API functions (for API key users with appropriate permissions)
export interface ApiKeyInfo {
  id: string
  name: string
  permissions: string[]
  createdAt: string
  lastUsed?: string
  isActive: boolean
}

export interface UsageStats {
  dailyUsed: number
  dailyLimit: number
  dailyRemaining: number
  minuteUsed: number
  minuteLimit: number
  minuteRemaining: number
  dailyReset: string
  minuteReset: string
}

export interface AccountStatus {
  plaidConnected: boolean
  googleSheetsConnected: boolean
  apiAccessEnabled: boolean
  subscriptionStatus?: string
  bankAccountsCount: number
  activeSpreadsheetsCount: number
}

// MCP Tool: List API keys (requires management:read permission)
export const listMyApiKeys = async (apiKey: string): Promise<{ apiKeys: ApiKeyInfo[] }> => {
  const url = `${API_BASE}/publicApiEndpoints/mcp/listMyApiKeys`

  return fetch(url, {
    method: 'GET',
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`API request failed: ${res.status} ${res.statusText}`)
    }
    return res.json()
  })
}

// MCP Tool: Check account status (requires management:read permission)
export const checkAccountStatus = async (apiKey: string): Promise<AccountStatus> => {
  const url = `${API_BASE}/publicApiEndpoints/mcp/checkAccountStatus`

  return fetch(url, {
    method: 'GET',
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`API request failed: ${res.status} ${res.statusText}`)
    }
    return res.json()
  })
}

// MCP Tool: View usage statistics (requires analytics:read permission)
export const viewUsageStats = async (apiKey: string): Promise<UsageStats> => {
  const url = `${API_BASE}/publicApiEndpoints/mcp/viewUsageStats`

  return fetch(url, {
    method: 'GET',
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`API request failed: ${res.status} ${res.statusText}`)
    }
    return res.json()
  })
}

// MCP Tool: Generate new API key (requires management:write permission)
export const generateNewApiKey = async (
  apiKey: string,
  request: { name: string; permissions?: string[] }
): Promise<{ id: string; name: string; key: string; permissions: string[]; createdAt: string }> => {
  const url = `${API_BASE}/publicApiEndpoints/mcp/generateNewApiKey`

  return fetch(url, {
    method: 'POST',
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`API request failed: ${res.status} ${res.statusText}`)
    }
    return res.json()
  })
}

export const getTransaction = async (
  apiKey: string,
  transactionId: string
): Promise<Transaction> => {
  return fetch(`${API_BASE}/publicApiEndpoints/getTransactions/${transactionId}`, {
    method: 'GET',
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`API request failed: ${res.status} ${res.statusText}`)
    }
    return res.json()
  })
}

export const getAccounts = async (apiKey: string): Promise<AccountsResponse> => {
  return fetch(`${API_BASE}/publicApiEndpoints/getAccounts`, {
    method: 'GET',
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`API request failed: ${res.status} ${res.statusText}`)
    }
    return res.json()
  })
}

export const getAccount = async (apiKey: string, accountId: string): Promise<Account> => {
  return fetch(`${API_BASE}/publicApiEndpoints/getAccounts/${accountId}`, {
    method: 'GET',
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`API request failed: ${res.status} ${res.statusText}`)
    }
    return res.json()
  })
}

export const getSheets = async (apiKey: string): Promise<SheetsResponse> => {
  return fetch(`${API_BASE}/getSheets`, {
    method: 'GET',
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`API request failed: ${res.status} ${res.statusText}`)
    }
    return res.json()
  })
}

export const getSheet = async (apiKey: string, sheetId: string): Promise<Spreadsheet> => {
  return fetch(`${API_BASE}/getSheets/${sheetId}`, {
    method: 'GET',
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`API request failed: ${res.status} ${res.statusText}`)
    }
    return res.json()
  })
}
